/**
 * The feature catalog — layer 1 of the three access layers in
 * `07-entitlements-rbac.md`. "Did the merchant buy this capability?"
 *
 * 89 codes. The source system had 83, three of which named a vendor, and a
 * vendor name in the catalogue means a catalogue edit and a migration per
 * market — the drift the bidirectional test exists to catch. They generalise:
 *
 * - `STORE_SETTINGS_ABA_INTEGRATION` + `STORE_SETTINGS_ACELEDA_INTEGRATION`
 *   -> `STORE_SETTINGS_PAYMENT_PROVIDER`, with per-provider availability on the
 *   market profile (`03-data-model.md` § 10.2, `07-entitlements-rbac.md`).
 * - `STORE_SETTINGS_GRAB_INTEGRATION` -> `STORE_SETTINGS_DELIVERY_PROVIDER`,
 *   by the same reasoning. `07-entitlements-rbac.md` lists the Grab code
 *   verbatim under "Not in this plan"; generalising it is our correction, and
 *   it is required by the standing rule against vendor names in the catalogue.
 *
 * `permissions.test.ts` asserts this list and the `requireFeature(...)`
 * literals in `apps/api/src` are equal in both directions.
 */

type FeatureDefinition = {
  readonly code: string;
  readonly name: string;
  readonly group: FeatureGroup;
  /** Purchasable on its own, outside a plan tier. */
  readonly isValueAdded: boolean;
  /**
   * An ascending scale, when this capability is sold at levels rather than
   * on/off. Absent means boolean.
   *
   * `06-saas-admin.md` § 3 finding 4: the published price list sells Promotion
   * & Membership at Basic and Premium, so at least one code needs a level. The
   * instruction is to model graded entitlements once, here, rather than
   * inventing `MARKETING_COUPON_PREMIUM`-style codes later — every such code is
   * another catalogue entry, another gate, and another chance for the two to
   * drift.
   */
  readonly grades?: readonly string[];
};

export const FEATURE_GROUPS = [
  "cashier",
  "data",
  "store_settings",
  "marketing",
  "item_management",
  "order_handling",
  "settlement",
  "inventory",
  "reservation",
  "chain",
] as const;

export type FeatureGroup = (typeof FEATURE_GROUPS)[number];

const DEFINITIONS = [
  // Cashier / terminal
  { code: "CASHIER_AVAILABLE", name: "Cashier Terminal", group: "cashier", isValueAdded: false },
  {
    code: "OFFLINE_CASHIER_AVAILABLE",
    name: "Cashier Terminal Offline Mode",
    group: "cashier",
    isValueAdded: true,
  },
  { code: "USER_AVAILABLE", name: "User Scan to Order", group: "cashier", isValueAdded: false },
  {
    code: "STORE_SETTINGS_REMOTE_ORDERING",
    name: "Remote Order",
    group: "cashier",
    isValueAdded: true,
  },

  // Data & reporting
  { code: "DATA_DASHBOARD", name: "Dashboard", group: "data", isValueAdded: false },
  { code: "DATA_MASTER_REPORT", name: "Master Report", group: "data", isValueAdded: false },
  {
    code: "DATA_ITEM_CATEGORY_REPORT",
    name: "Item and Category Report",
    group: "data",
    isValueAdded: false,
  },
  {
    code: "DATA_HOURLY_SALES_REPORT",
    name: "Hourly Sales Report",
    group: "data",
    isValueAdded: false,
  },
  {
    code: "DATA_HOURLY_SALES_DETAILS_REPORT",
    name: "Hourly Sales Details Report",
    group: "data",
    isValueAdded: false,
  },
  {
    code: "DATA_DISCOUNT_REPORT",
    name: "Discount Transaction Report",
    group: "data",
    isValueAdded: false,
  },
  { code: "DATA_VOID_REPORT", name: "Void Report", group: "data", isValueAdded: false },
  {
    code: "DATA_TOP_SELLING_ITEM_REPORT",
    name: "Top Selling Item Report",
    group: "data",
    isValueAdded: false,
  },
  {
    code: "DATA_NEW_ITEM_SALES_REPORT",
    name: "New Product Sales Report",
    group: "data",
    isValueAdded: false,
  },
  { code: "DATA_COUPON_REPORT", name: "Coupon Report", group: "data", isValueAdded: false },
  {
    code: "DATA_DISCOUNT_PROMO_REPORT",
    name: "Discount Promo Report",
    group: "data",
    isValueAdded: false,
  },
  { code: "DATA_DAILY_REPORT", name: "Daily Report", group: "data", isValueAdded: false },
  {
    code: "DATA_STORE_COST_REPORT",
    name: "Store Costs Report",
    group: "data",
    isValueAdded: false,
  },
  { code: "DATA_ITEM_COST_REPORT", name: "Item Costs Report", group: "data", isValueAdded: false },
  { code: "DATA_SALES_REPORT", name: "Sales Report", group: "data", isValueAdded: false },
  { code: "DATA_ITEM_REPORT", name: "Item Report", group: "data", isValueAdded: false },
  {
    code: "DATA_PAYMENT_METHOD_REPORT",
    name: "Payment Method Report",
    group: "data",
    isValueAdded: false,
  },
  { code: "DATA_PROFIT_REPORT", name: "Profit Report", group: "data", isValueAdded: false },
  { code: "DATA_TABLE_REPORT", name: "Table Report", group: "data", isValueAdded: false },
  { code: "DATA_SHIFT_REPORT", name: "Shift Record", group: "data", isValueAdded: false },
  { code: "DATA_INVENTORY_REPORT", name: "Inventory Report", group: "data", isValueAdded: false },
  { code: "DATA_CREDIT_REPORT", name: "Credit Report", group: "data", isValueAdded: false },
  { code: "DATA_TIP_REPORT", name: "Tip Reporting", group: "data", isValueAdded: false },
  {
    code: "DATA_STORE_BUSINESS_INSIGHTS",
    name: "Business Insights",
    group: "data",
    isValueAdded: true,
  },

  // Store settings
  {
    code: "STORE_SETTINGS_STAFF_MANAGEMENT",
    name: "Staff Mgmt",
    group: "store_settings",
    isValueAdded: false,
  },
  {
    code: "STORE_SETTINGS_STORE_INFO",
    name: "Basic Store Configure",
    group: "store_settings",
    isValueAdded: false,
  },
  {
    code: "STORE_SETTINGS_VAT",
    name: "Tax Configure",
    group: "store_settings",
    isValueAdded: false,
  },
  {
    code: "STORE_SETTINGS_SERVICE_FEE",
    name: "Service Fee Configure",
    group: "store_settings",
    isValueAdded: false,
  },
  {
    code: "STORE_SETTINGS_TABLE_FEE",
    name: "Table Fee",
    group: "store_settings",
    isValueAdded: false,
  },
  {
    code: "MARKETING_GIFT_CARD",
    name: "Gift Cards",
    group: "marketing",
    isValueAdded: false,
  },
  {
    code: "CHAIN_ROYALTY",
    name: "Franchise Royalties",
    group: "store_settings",
    isValueAdded: true,
  },
  {
    code: "HR_TIMECLOCK",
    name: "Timeclock",
    group: "store_settings",
    isValueAdded: false,
  },
  {
    code: "STORE_SETTINGS_TIPPING",
    name: "Tipping",
    group: "store_settings",
    isValueAdded: false,
  },
  {
    code: "STORE_SETTINGS_ITEM_NOT_DISCOUNT",
    name: "Items Not Included in the Discount Configure",
    group: "store_settings",
    isValueAdded: false,
  },
  {
    code: "STORE_SETTINGS_ADS",
    name: "Advertisement Management",
    group: "store_settings",
    isValueAdded: false,
  },
  {
    code: "STORE_SETTINGS_QUICK_REMARK",
    name: "Quick Remarks",
    group: "store_settings",
    isValueAdded: false,
  },
  {
    code: "STORE_SETTINGS_INVOICE_NUMBER",
    name: "Tax Information and Invoice Number",
    group: "store_settings",
    isValueAdded: false,
  },
  {
    code: "STORE_SETTINGS_RECEIPT_TEMPLATE",
    name: "Template Configure",
    group: "store_settings",
    isValueAdded: false,
  },
  {
    code: "STORE_SETTINGS_PRINTER_PAIRING",
    name: "Printer Configure",
    group: "store_settings",
    isValueAdded: false,
  },
  {
    code: "STORE_SETTINGS_AUTO_ACCEPT_ORDER",
    name: "Auto-accept Order Configure",
    group: "store_settings",
    isValueAdded: false,
  },
  {
    code: "STORE_SETTINGS_PUSH_MESSAGE",
    name: "Push Notification",
    group: "store_settings",
    isValueAdded: false,
  },
  {
    code: "STORE_SETTINGS_VOICE_BROADCAST",
    name: "Voice Broadcast",
    group: "store_settings",
    isValueAdded: false,
  },
  {
    code: "STORE_SETTINGS_CHANGE_SHIFT",
    name: "Change Shift",
    group: "store_settings",
    isValueAdded: false,
  },
  {
    code: "STORE_SETTINGS_DOUBLE_CURRENCY",
    name: "Supports Dual-currency Settlement",
    group: "store_settings",
    isValueAdded: false,
  },
  {
    code: "STORE_SETTINGS_DELIVERY_ITEM",
    name: "Delivery Item Configure",
    group: "store_settings",
    isValueAdded: false,
  },
  {
    code: "STORE_SETTINGS_PAYMENT_METHOD",
    name: "Payment Method Configure",
    group: "store_settings",
    isValueAdded: false,
  },
  {
    code: "STORE_SETTINGS_PAYMENT_PROVIDER",
    name: "Third-party payment provider integration",
    group: "store_settings",
    isValueAdded: true,
  },
  {
    code: "STORE_SETTINGS_DELIVERY_PROVIDER",
    name: "Delivery platform integration",
    group: "store_settings",
    isValueAdded: true,
  },

  // Marketing
  {
    code: "MARKETING_ITEM_PRICE_PROMO",
    name: "Strikethrough Price",
    group: "marketing",
    isValueAdded: false,
  },
  {
    code: "MARKETING_PROMOTIONAL_CAMPAIGN",
    name: "Promotional Campaign",
    group: "marketing",
    isValueAdded: false,
  },
  { code: "MARKETING_DISCOUNT", name: "Discount", group: "marketing", isValueAdded: false },
  { code: "MARKETING_COUPON", name: "Coupon", group: "marketing", isValueAdded: false },
  {
    code: "MARKETING_MEMBER",
    name: "Member Mgmt",
    group: "marketing",
    isValueAdded: false,
    // Which capabilities sit at which level is our mapping, not something the
    // source revealed: the published catalog has one "Promotion & Membership"
    // row reading "Basic Functions" and "Premium Functions" without listing
    // what separates them. We read basic as member records and manual points,
    // premium as stored value, top-up tiers and chain-shared balance.
    grades: ["basic", "premium"],
  },

  // Item management
  { code: "ITEM_MGMT_SET_MEAL", name: "Meal Set", group: "item_management", isValueAdded: false },
  {
    code: "ITEM_MGMT_DAILY_PRICE",
    name: "Daily Price",
    group: "item_management",
    isValueAdded: false,
  },
  { code: "ITEM_MGMT_WEIGHT", name: "Weighed Item", group: "item_management", isValueAdded: false },
  {
    code: "ITEM_MGMT_TEMPORARY_ITEM",
    name: "Temporary Item",
    group: "item_management",
    isValueAdded: false,
  },
  {
    code: "ITEM_MGMT_PRE_ORDER_ITEM",
    name: "Pre-order Item",
    group: "item_management",
    isValueAdded: false,
  },
  {
    code: "ITEM_MGMT_DISPLAY_SALES",
    name: "Display Item Sales",
    group: "item_management",
    isValueAdded: false,
  },
  { code: "ITEM_MGMT_TAG", name: "Item Tag", group: "item_management", isValueAdded: false },
  {
    code: "ITEM_MGMT_PACK_FEE",
    name: "Pack Fee Configure",
    group: "item_management",
    isValueAdded: false,
  },
  {
    code: "ITEM_MGMT_SPECIFICATION",
    name: "Specification Group",
    group: "item_management",
    isValueAdded: false,
  },
  {
    code: "ITEM_MGMT_TOPPING",
    name: "Item Toppings",
    group: "item_management",
    isValueAdded: false,
  },

  // Order handling
  {
    code: "ORDER_CUSTOMER_INFO",
    name: "Add Customer Information",
    group: "order_handling",
    isValueAdded: false,
  },
  { code: "ORDER_STAFF_ORDER", name: "Staff Order", group: "order_handling", isValueAdded: false },
  {
    code: "ORDER_CHANGE_TABLE",
    name: "Change Table",
    group: "order_handling",
    isValueAdded: false,
  },
  {
    code: "ORDER_PENDING_RESUME",
    name: "Pending/Resume",
    group: "order_handling",
    isValueAdded: false,
  },
  { code: "ORDER_ADD_MORE", name: "Order More", group: "order_handling", isValueAdded: false },
  { code: "ORDER_RETURN", name: "Return Item", group: "order_handling", isValueAdded: false },
  { code: "ORDER_CANCEL", name: "Cancel Order", group: "order_handling", isValueAdded: false },
  { code: "ORDER_PRINT_RECEIPT", name: "Reprint", group: "order_handling", isValueAdded: false },
  {
    code: "ORDER_EXPORT_RECEIPT",
    name: "Export Receipt",
    group: "order_handling",
    isValueAdded: false,
  },
  { code: "ORDER_FREE_VAT", name: "Tax-Free", group: "order_handling", isValueAdded: false },
  {
    code: "ORDER_MERGE_SPLIT",
    name: "Merge Order/Split Order",
    group: "order_handling",
    isValueAdded: false,
  },

  // Settlement
  { code: "SETTLEMENT_PAYMENT", name: "Pay", group: "settlement", isValueAdded: false },
  { code: "SETTLEMENT_REFUND", name: "Refund", group: "settlement", isValueAdded: false },
  { code: "SETTLEMENT_CREDIT", name: "Credit Pay", group: "settlement", isValueAdded: false },
  {
    code: "SETTLEMENT_REVERSE",
    name: "Reverse Check-out",
    group: "settlement",
    isValueAdded: false,
  },
  {
    code: "SETTLEMENT_ON_ITEM_DISCOUNT",
    name: "On Item Discount",
    group: "settlement",
    isValueAdded: false,
  },
  {
    code: "SETTLEMENT_WHOLE_BILL_DISCOUNT",
    name: "Whole Bill Discount",
    group: "settlement",
    isValueAdded: false,
  },
  {
    // A tender that is money only for a particular class of goods — what a
    // government food-assistance card is. Named for the mechanism rather than
    // for any one programme, because every market runs its own and a name here
    // would be a migration per market.
    code: "SETTLEMENT_RESTRICTED_TENDER",
    name: "Restricted Tender",
    group: "settlement",
    isValueAdded: true,
  },

  // Inventory
  { code: "INVENTORY", name: "Inventory Mgmt Modules", group: "inventory", isValueAdded: false },
  {
    code: "INVENTORY_LINK_ITEM",
    name: "Item Associated and Clearance Sale",
    group: "inventory",
    isValueAdded: false,
  },

  // Reservations
  {
    // Bookings and the waitlist are one purchase. A restaurant that takes
    // bookings runs a door list on the same night and by the same person, and a
    // merchant who bought one and found the other behind a second paywall would
    // be right to be annoyed.
    code: "RESERVATION",
    name: "Reservations and Waitlist",
    group: "reservation",
    isValueAdded: true,
  },

  // Chain
  { code: "CHAIN_STORE", name: "Chain Store", group: "chain", isValueAdded: true },
] as const satisfies readonly FeatureDefinition[];

export type FeatureCode = (typeof DEFINITIONS)[number]["code"];

export const FEATURE_CATALOG: readonly FeatureDefinition[] = DEFINITIONS;

export const FEATURE_CODES = DEFINITIONS.map((f) => f.code) as unknown as readonly [
  FeatureCode,
  ...FeatureCode[],
];

const BY_CODE = new Map<string, FeatureDefinition>(DEFINITIONS.map((f) => [f.code, f]));

export function isFeatureCode(value: string): value is FeatureCode {
  return BY_CODE.has(value);
}

export function feature(code: FeatureCode): FeatureDefinition {
  const found = BY_CODE.get(code);
  if (!found) throw new Error(`Unknown feature code: ${code}`);
  return found;
}

/**
 * Codes that are grantable before any route enforces them — the escape hatch
 * Poseidon's `permissions.test.ts` uses, per `13-house-style.md` § 5.1.
 *
 * Hand-maintained and **shrink-only**. Every entry is a feature a plan can
 * already sell but no `requireFeature(...)` yet guards. As each phase lands its
 * routes, its codes are deleted from here — deleting one is the moment the gate
 * becomes real, which is why it is a deliberate hand edit and not a generated
 * list. Phase 0 enforces nothing, so it starts as the whole catalog.
 */
export const RESERVED_FEATURES: readonly FeatureCode[] = [];

/**
 * Compares a held grade against a required one on the same feature.
 *
 * Ordering comes from the feature's own scale, so "premium" outranks "basic"
 * because the catalog says so and not because of how the strings sort.
 */
export function gradeAtLeast(code: FeatureCode, held: string | null, required: string): boolean {
  const scale = feature(code).grades;
  if (!scale) {
    throw new Error(`Feature ${code} is not graded; use a plain entitlement check.`);
  }
  const requiredIndex = scale.indexOf(required);
  if (requiredIndex === -1) {
    throw new Error(`Feature ${code} has no grade "${required}". Scale: ${scale.join(", ")}.`);
  }
  if (held === null) return false;
  const heldIndex = scale.indexOf(held);
  if (heldIndex === -1) {
    throw new Error(`Feature ${code} has no grade "${held}". Scale: ${scale.join(", ")}.`);
  }
  return heldIndex >= requiredIndex;
}

export function isGraded(code: FeatureCode): boolean {
  return feature(code).grades !== undefined;
}

/**
 * A resolved entitlement: the code, and the grade held where the feature is
 * graded. `07-entitlements-rbac.md` resolves the set as the union of the plan's
 * features, add-ons within their own window, and anything held permanently.
 */
export type FeatureGrant = {
  readonly code: FeatureCode;
  readonly grade: string | null;
};
